import {
  CANCELLATION_VARIABLES,
  INVITE_COPY_VARIABLES,
  REMINDER_VARIABLES,
} from "../../../constants/promotionEmailVariables";

export const SETTINGS_SUBJECT_LIMIT = 80;

export { CANCELLATION_VARIABLES, INVITE_COPY_VARIABLES };

export const RSVP_REMINDER_VARIABLES = REMINDER_VARIABLES;

export const TRIGGER_PRESETS = [
  { value: 15, unit: "minutes" },
  { value: 1, unit: "hours" },
  { value: 1, unit: "days" },
  { value: 3, unit: "days" },
];

export const DEFAULT_RSVP_SCHEDULE = {
  enabled: true,
  triggerEnabled: true,
  trigger: { value: 3, unit: "days" },
  repeats: [],
  cutoffEnabled: false,
  cutoff: { value: 24, unit: "hours" },
};

export const offsetKey = ({ value, unit } = {}) =>
  `${Number(value) || 0}-${unit || "hours"}`;

export const sameOffset = (a, b) =>
  Number(a?.value) === Number(b?.value) && a?.unit === b?.unit;

export const offsetLabel = (
  { value, unit } = {},
  { eventSuffix = false } = {}
) => {
  const n = Number(value) || 0;
  const unitLabel =
    unit === "minutes"
      ? n === 1
        ? "Minute"
        : "Minutes"
      : unit === "hours"
        ? n === 1
          ? "Hour"
          : "Hours"
        : n === 1
          ? "Day"
          : "Days";
  return eventSuffix
    ? `${n} ${unitLabel} Before Event`
    : `${n} ${unitLabel} Before`;
};

export const isPresetOffset = (offset, presets) =>
  presets.some((item) => sameOffset(item, offset));

export const parseTemplateSegments = (value) => {
  const text = String(value || "");
  const parts = [];
  const re = /\{\{\s*(\w+)\s*\}\}/g;
  let last = 0;
  let match;
  while ((match = re.exec(text))) {
    if (match.index > last) {
      parts.push({ type: "text", value: text.slice(last, match.index) });
    }
    parts.push({ type: "var", key: match[1] });
    last = match.index + match[0].length;
  }
  if (last < text.length || parts.length === 0) {
    parts.push({ type: "text", value: text.slice(last) });
  }
  return parts;
};

export const serializeTemplateSegments = (parts) =>
  parts
    .map((part) =>
      part.type === "var" ? `{{${part.key}}}` : String(part.value || "")
    )
    .join("");

export const insertVariable = (value, key, start, end) => {
  const token = `{{${key}}}`;
  const text = String(value || "");
  const from = Number.isFinite(start) ? start : text.length;
  const to = Number.isFinite(end) ? end : from;
  return `${text.slice(0, from)}${token}${text.slice(to)}`;
};

export const removeSegmentAt = (value, index) => {
  const parts = parseTemplateSegments(value).filter((_, i) => i !== index);
  return serializeTemplateSegments(parts);
};

export const createVariableChip = (key, label) => {
  const chip = document.createElement("span");
  chip.className = "email-var-chip";
  chip.contentEditable = "false";
  chip.dataset.var = key;
  chip.append(document.createTextNode(label || key));
  const remove = document.createElement("span");
  remove.setAttribute("role", "button");
  remove.tabIndex = -1;
  remove.dataset.removeVar = "true";
  remove.setAttribute("aria-label", `Remove ${label || key}`);
  remove.textContent = "×";
  chip.append(remove);
  return chip;
};

export const fillChipEditor = (root, value, labelFor = (key) => key) => {
  root.replaceChildren();
  const parts = parseTemplateSegments(value);
  const visible = parts.filter(
    (part) => part.type === "var" || String(part.value || "").length > 0
  );
  if (!visible.length) {
    root.append(document.createTextNode(""));
    return;
  }
  visible.forEach((part) => {
    if (part.type === "var") {
      root.append(createVariableChip(part.key, labelFor(part.key)));
      return;
    }
    root.append(document.createTextNode(part.value));
  });
};

export const serializeChipEditor = (root) => {
  let out = "";
  const walk = (node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      out += String(node.textContent || "")
        .replace(/\u200B/g, "")
        .replace(/\u00a0/g, " ");
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    if (node.dataset?.var) {
      out += `{{${node.dataset.var}}}`;
      return;
    }
    if (node.tagName === "BR") {
      out += "\n";
      return;
    }
    const block = node.tagName === "DIV" || node.tagName === "P";
    if (block && out && !out.endsWith("\n")) out += "\n";
    node.childNodes.forEach(walk);
  };
  root.childNodes.forEach(walk);
  return out;
};

const isSkippableCaretNode = (node) =>
  node?.nodeType === Node.TEXT_NODE &&
  String(node.textContent || "").replace(/\u200B/g, "") === "";

export const chipBesideCaret = (root, range, direction) => {
  if (!root || !range?.collapsed) return null;
  let node = null;
  if (range.startContainer === root) {
    node =
      direction === "back"
        ? root.childNodes[range.startOffset - 1]
        : root.childNodes[range.startOffset];
  } else if (range.startContainer.nodeType === Node.TEXT_NODE) {
    const text = range.startContainer;
    const before = text.textContent.slice(0, range.startOffset).replace(/\u200B/g, "");
    const after = text.textContent.slice(range.startOffset).replace(/\u200B/g, "");
    if (direction === "back" && before === "") node = text.previousSibling;
    if (direction === "forward" && after === "") node = text.nextSibling;
  }
  while (isSkippableCaretNode(node)) {
    node = direction === "back" ? node.previousSibling : node.nextSibling;
  }
  return node?.dataset?.var ? node : null;
};

export const insertLineBreakAtRange = (root, range) => {
  const br = document.createElement("br");
  const target = range && root.contains(range.startContainer) ? range : null;
  if (!target) {
    root.append(br);
  } else {
    target.deleteContents();
    target.insertNode(br);
  }
  let text = br.nextSibling;
  if (!text || text.nodeType !== Node.TEXT_NODE) {
    text = document.createTextNode("\u200B");
    br.after(text);
  }
  const after = document.createRange();
  after.setStart(text, 0);
  after.collapse(true);
  return after;
};

export const insertChipAtRange = (root, range, key, label) => {
  const chip = createVariableChip(key, label);
  const target =
    range && root.contains(range.startContainer) ? range : null;
  if (!target) {
    root.append(chip);
  } else {
    target.deleteContents();
    target.insertNode(chip);
  }
  let text = chip.nextSibling;
  if (!text || text.nodeType !== Node.TEXT_NODE) {
    text = document.createTextNode("\u200B");
    chip.after(text);
  } else if (!text.textContent.startsWith("\u200B")) {
    text.textContent = `\u200B${text.textContent}`;
  }
  const after = document.createRange();
  after.setStart(text, 1);
  after.collapse(true);
  return after;
};
